import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CancelIcon from '@mui/icons-material/Cancel'
import SyncIcon from '@mui/icons-material/Sync'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import DownloadIcon from '@mui/icons-material/Download'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import {
  financeService,
  PaymentDto,
  PayoutDto,
  FinanceListParams,
} from '../api/adminServices'
import { formatCurrency } from '../utils/formatCurrency'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import { exportCsv } from '../utils/exportCsv'
import ConfirmDialog from '../components/ConfirmDialog'
import { useToast } from '../components/useToast'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'
import { PropertyView, PropertySection, PropertyField } from '../components/PropertyView'

type TabKey = 'payments' | 'payouts' | 'wires'

const PAGE_SIZE = 20

const PAYMENT_STATUSES = [
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
  'CANCELLED',
]

const PAYOUT_STATUSES = [
  'PENDING',
  'SCHEDULED',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
]

function statusColor(
  status?: string
): 'warning' | 'success' | 'error' | 'info' | 'default' {
  if (!status) return 'default'
  if (status === 'COMPLETED' || status === 'PAID' || status === 'RECONCILED') return 'success'
  if (
    status === 'FAILED' ||
    status === 'OVERDUE' ||
    status === 'CANCELLED' ||
    status === 'REFUNDED'
  )
    return 'error'
  if (
    status === 'PENDING' ||
    status === 'SCHEDULED' ||
    status === 'PROCESSING' ||
    status === 'PARTIALLY_REFUNDED'
  )
    return 'warning'
  return 'default'
}

function amountOf(p: PaymentDto): number {
  return p.grossAmount ?? p.amount ?? 0
}

function payoutDueDateOf(p: PayoutDto): string | undefined {
  return p.dueDate || p.payoutDate || p.scheduledDate
}

function payoutPaidDateOf(p: PayoutDto): string | undefined {
  return p.paidDate || p.processedAt
}

function paymentRefOf(p: PaymentDto): string {
  return p.bankWireReference || p.referenceNumber || ''
}

function paidDateOf(p: PaymentDto): string | undefined {
  return p.paidDate || p.paymentDate
}

export default function FinancePage() {
  const [tab, setTab] = useState<TabKey>('payments')
  const [page, setPage] = useState(0)
  const [statusFilter, setStatusFilter] = useState('')
  const [accountFilter, setAccountFilter] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [search, setSearch] = useState('')
  const [wireSubTab, setWireSubTab] = useState<'pending' | 'overdue'>('pending')

  // Detail / action dialog state
  const [selectedPayment, setSelectedPayment] = useState<PaymentDto | null>(null)
  const [selectedPayout, setSelectedPayout] = useState<PayoutDto | null>(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)

  const [refundDialogOpen, setRefundDialogOpen] = useState(false)
  const [refundAmount, setRefundAmount] = useState('')
  const [refundReason, setRefundReason] = useState('')

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')

  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false)
  const [reconcilePaymentId, setReconcilePaymentId] = useState<string | null>(null)
  const [reconcileRef, setReconcileRef] = useState('')
  const [reconcileNotes, setReconcileNotes] = useState('')

  // Typed-confirmation gates for destructive actions
  const [refundConfirmOpen, setRefundConfirmOpen] = useState(false)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)

  const toast = useToast()
  const queryClient = useQueryClient()
  const getLocalizedLabel = useLocalizedLabel()

  const listParams: FinanceListParams = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      status: statusFilter || undefined,
      accountId: accountFilter || undefined,
      startDate: startDate ? `${startDate}T00:00:00` : undefined,
      endDate: endDate ? `${endDate}T23:59:59` : undefined,
    }),
    [page, statusFilter, accountFilter, startDate, endDate]
  )

  // Paginated payments / payouts
  const paymentsQuery = useQuery({
    queryKey: ['admin-payments', listParams],
    queryFn: () => financeService.getAllPayments(listParams),
    enabled: tab === 'payments',
  })
  const payoutsQuery = useQuery({
    queryKey: ['admin-payouts', listParams],
    queryFn: () => financeService.getAllPayouts(listParams),
    enabled: tab === 'payouts',
  })

  // Wire transfers (non-paginated; small lists)
  const pendingWiresQuery = useQuery({
    queryKey: ['wires-pending'],
    queryFn: () => financeService.getPendingWireTransfers(),
    enabled: tab === 'wires' && wireSubTab === 'pending',
  })
  const overdueWiresQuery = useQuery({
    queryKey: ['wires-overdue'],
    queryFn: () => financeService.getOverdueWireTransfers(),
    enabled: tab === 'wires' && wireSubTab === 'overdue',
  })

  // Mutations
  const refundMutation = useMutation({
    mutationFn: ({
      paymentId,
      request,
    }: {
      paymentId: string
      request: { amount: number; reason: string }
    }) => financeService.refundPayment(paymentId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payments'] })
      setRefundConfirmOpen(false)
      setRefundDialogOpen(false)
      setViewDialogOpen(false)
      setRefundAmount('')
      setRefundReason('')
      toast.success('Refund processed successfully')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to process refund'),
  })

  const cancelPayoutMutation = useMutation({
    mutationFn: ({ payoutId, reason }: { payoutId: string; reason: string }) =>
      financeService.cancelPayout(payoutId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payouts'] })
      setCancelConfirmOpen(false)
      setCancelDialogOpen(false)
      setViewDialogOpen(false)
      setCancelReason('')
      toast.success('Payout cancelled successfully')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to cancel payout'),
  })

  const reconcileMutation = useMutation({
    mutationFn: ({
      paymentId,
      request,
    }: {
      paymentId: string
      request: { bankReference: string; notes?: string }
    }) => financeService.reconcileWireTransfer(paymentId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wires-pending'] })
      queryClient.invalidateQueries({ queryKey: ['wires-overdue'] })
      setReconcileDialogOpen(false)
      setReconcilePaymentId(null)
      setReconcileRef('')
      setReconcileNotes('')
      toast.success('Wire transfer reconciled successfully')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to reconcile wire transfer'),
  })

  const triggerPaymentsMutation = useMutation({
    mutationFn: () => financeService.triggerProcessPayments(),
    onSuccess: () => toast.success('Payment processing triggered'),
    onError: (e) =>
      toast.errorFrom(
        e,
        'Trigger failed — finance-service-jobs pod may not be running.'
      ),
  })
  const triggerPayoutsMutation = useMutation({
    mutationFn: () => financeService.triggerProcessPayouts(),
    onSuccess: () => toast.success('Payout processing triggered'),
    onError: (e) =>
      toast.errorFrom(
        e,
        'Trigger failed — finance-service-jobs pod may not be running.'
      ),
  })

  // Loaders
  const handleViewPayment = async (paymentId: string) => {
    try {
      const payment = await financeService.getPayment(paymentId)
      setSelectedPayment(payment)
      setSelectedPayout(null)
      setViewDialogOpen(true)
    } catch (e) {
      toast.errorFrom(e, 'Failed to load payment')
    }
  }
  const handleViewPayout = async (payoutId: string) => {
    try {
      const payout = await financeService.getPayout(payoutId)
      setSelectedPayout(payout)
      setSelectedPayment(null)
      setViewDialogOpen(true)
    } catch (e) {
      toast.errorFrom(e, 'Failed to load payout')
    }
  }

  // Refresh
  const handleRefresh = () => {
    if (tab === 'payments') paymentsQuery.refetch()
    else if (tab === 'payouts') payoutsQuery.refetch()
    else if (wireSubTab === 'pending') pendingWiresQuery.refetch()
    else overdueWiresQuery.refetch()
  }

  // Reset paging when filter changes
  const onFilterChange = (setter: (v: string) => void, value: string) => {
    setter(value)
    setPage(0)
  }

  // Extract rows from paged response
  const paymentsRows: PaymentDto[] = paymentsQuery.data?.content ?? []
  const paymentsTotalPages: number = paymentsQuery.data?.totalPages ?? 0
  const payoutsRows: PayoutDto[] = payoutsQuery.data?.content ?? []
  const payoutsTotalPages: number = payoutsQuery.data?.totalPages ?? 0
  const wireRows: PaymentDto[] =
    wireSubTab === 'pending' ? pendingWiresQuery.data ?? [] : overdueWiresQuery.data ?? []

  // Client-side search filter (free-text on id / refs)
  const searchFilter = (s: string) => (r: PaymentDto | PayoutDto) => {
    if (!s) return true
    const q = s.toLowerCase()
    return (
      r.id.toLowerCase().includes(q) ||
      (r as PaymentDto).bankWireReference?.toLowerCase().includes(q) ||
      (r as PaymentDto).invoiceNumber?.toLowerCase().includes(q) ||
      (r.payerAccountId || '').toLowerCase().includes(q) ||
      (r.payeeAccountId || '').toLowerCase().includes(q) ||
      false
    )
  }

  const visiblePayments = paymentsRows.filter(searchFilter(search))
  const visiblePayouts = payoutsRows.filter(searchFilter(search))
  const visibleWires = wireRows.filter(searchFilter(search))

  // KPI tiles derived from the currently visible page (best-effort without aggregate endpoint)
  const kpis = useMemo(() => {
    if (tab === 'payments') {
      const sum = visiblePayments.reduce((acc, p) => acc + amountOf(p), 0)
      const pendingCount = visiblePayments.filter((p) => p.status === 'PENDING').length
      const failedCount = visiblePayments.filter((p) => p.status === 'FAILED').length
      return [
        { label: 'On page', value: visiblePayments.length.toString() },
        { label: 'Sum on page', value: formatCurrency(sum) },
        { label: 'Pending on page', value: pendingCount.toString(), color: 'warning.main' },
        { label: 'Failed on page', value: failedCount.toString(), color: 'error.main' },
      ]
    }
    if (tab === 'payouts') {
      const sum = visiblePayouts.reduce((acc, p) => acc + (p.amount || 0), 0)
      const scheduled = visiblePayouts.filter(
        (p) => p.status === 'SCHEDULED' || p.status === 'PENDING'
      ).length
      const failed = visiblePayouts.filter((p) => p.status === 'FAILED').length
      return [
        { label: 'On page', value: visiblePayouts.length.toString() },
        { label: 'Sum on page', value: formatCurrency(sum) },
        { label: 'Scheduled on page', value: scheduled.toString(), color: 'info.main' },
        { label: 'Failed on page', value: failed.toString(), color: 'error.main' },
      ]
    }
    const sum = visibleWires.reduce((acc, p) => acc + amountOf(p), 0)
    return [
      { label: wireSubTab === 'pending' ? 'Pending wires' : 'Overdue wires', value: visibleWires.length.toString() },
      { label: 'Sum', value: formatCurrency(sum), color: wireSubTab === 'overdue' ? 'error.main' : undefined },
    ]
  }, [tab, wireSubTab, visiblePayments, visiblePayouts, visibleWires])

  // CSV export per tab
  const handleExport = () => {
    if (tab === 'payments') {
      exportCsv<PaymentDto>(`payments-${new Date().toISOString().slice(0, 10)}`,
        [
          { header: 'ID', field: 'id' },
          { header: 'Status', field: 'status' },
          { header: 'Gross Amount', field: (r) => amountOf(r) },
          { header: 'Currency', field: 'currency' },
          { header: 'Payer Account', field: 'payerAccountId' },
          { header: 'Payee Account', field: 'payeeAccountId' },
          { header: 'Terms', field: 'terms' },
          { header: 'Invoice #', field: 'invoiceNumber' },
          { header: 'Due Date', field: 'dueDate' },
          { header: 'Paid Date', field: (r) => paidDateOf(r) || '' },
          { header: 'Wire Ref', field: 'bankWireReference' },
          { header: 'Refunded', field: 'refundedAmount' },
          { header: 'Created', field: 'createdAt' },
        ],
        visiblePayments)
    } else if (tab === 'payouts') {
      exportCsv<PayoutDto>(`payouts-${new Date().toISOString().slice(0, 10)}`,
        [
          { header: 'ID', field: 'id' },
          { header: 'Status', field: 'status' },
          { header: 'Amount', field: 'amount' },
          { header: 'Currency', field: 'currency' },
          { header: 'Payee Account', field: 'payeeAccountId' },
          { header: 'Terms', field: 'terms' },
          { header: 'Due Date', field: (r) => payoutDueDateOf(r) || '' },
          { header: 'Paid Date', field: (r) => payoutPaidDateOf(r) || '' },
          { header: 'Failure', field: 'failureReason' },
          { header: 'Created', field: 'createdAt' },
        ],
        visiblePayouts)
    } else {
      exportCsv<PaymentDto>(`wire-transfers-${wireSubTab}-${new Date().toISOString().slice(0, 10)}`,
        [
          { header: 'ID', field: 'id' },
          { header: 'Status', field: 'status' },
          { header: 'Amount', field: (r) => amountOf(r) },
          { header: 'Currency', field: 'currency' },
          { header: 'Payer Account', field: 'payerAccountId' },
          { header: 'Bank Reference', field: 'bankWireReference' },
          { header: 'Invoice #', field: 'invoiceNumber' },
          { header: 'Reminders', field: 'reminderCount' },
          { header: 'Last Reminder', field: 'lastReminderSent' },
          { header: 'Created', field: 'createdAt' },
        ],
        visibleWires)
    }
  }

  const isLoading =
    (tab === 'payments' && paymentsQuery.isLoading) ||
    (tab === 'payouts' && payoutsQuery.isLoading) ||
    (tab === 'wires' && wireSubTab === 'pending' && pendingWiresQuery.isLoading) ||
    (tab === 'wires' && wireSubTab === 'overdue' && overdueWiresQuery.isLoading)

  // Active query for the current tab — drives error / retry state
  const activeQuery =
    tab === 'payments'
      ? paymentsQuery
      : tab === 'payouts'
      ? payoutsQuery
      : wireSubTab === 'pending'
      ? pendingWiresQuery
      : overdueWiresQuery
  const isError = activeQuery.isError
  const error = activeQuery.error
  const colSpan = tab === 'wires' ? 9 : tab === 'payments' ? 10 : 9

  // Validation
  const refundReasonValid = refundReason.trim().length >= 10 && refundReason.trim().length <= 500
  const refundAmountValid =
    selectedPayment != null &&
    parseFloat(refundAmount) > 0 &&
    parseFloat(refundAmount) <= amountOf(selectedPayment)
  const cancelReasonValid =
    cancelReason.trim().length >= 10 && cancelReason.trim().length <= 500
  const reconcileRefValid = reconcileRef.trim().length >= 5 && reconcileRef.trim().length <= 100

  const activeRowsEmpty =
    tab === 'payments'
      ? visiblePayments.length === 0
      : tab === 'payouts'
      ? visiblePayouts.length === 0
      : visibleWires.length === 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={2}
        mb={2}
      >
        <Stack direction="row" spacing={1}>
          <Tooltip title="Trigger scheduled payment processing">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={() => triggerPaymentsMutation.mutate()}
                disabled={triggerPaymentsMutation.isPending}
              >
                Run Payments Job
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Trigger scheduled payout processing">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={() => triggerPayoutsMutation.mutate()}
                disabled={triggerPayoutsMutation.isPending}
              >
                Run Payouts Job
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export current view as CSV">
            <IconButton onClick={handleExport}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh current tab">
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* KPI tiles */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {kpis.map((kpi) => (
          <Grid item xs={6} sm={3} key={kpi.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {kpi.label}
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={kpi.color ? { color: kpi.color } : undefined}
              >
                {kpi.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Tabs
        value={tab}
        onChange={(_e, v) => {
          setTab(v)
          setPage(0)
          setStatusFilter('')
        }}
        sx={{ mb: 2 }}
      >
        <Tab label="Payments" value="payments" />
        <Tab label="Payouts" value="payouts" />
        <Tab label="Wire Transfers" value="wires" />
      </Tabs>

      {/* Filter bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          {tab !== 'wires' && (
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => onFilterChange(setStatusFilter, e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {(tab === 'payments' ? PAYMENT_STATUSES : PAYOUT_STATUSES).map((s) => (
                <MenuItem key={s} value={s}>
                  {getLocalizedLabel('paymentStatus', s) || s}
                </MenuItem>
              ))}
            </TextField>
          )}
          {tab === 'wires' && (
            <Tabs
              value={wireSubTab}
              onChange={(_e, v) => setWireSubTab(v)}
              sx={{ minHeight: 0 }}
            >
              <Tab label="Pending" value="pending" sx={{ minHeight: 0, py: 0.5 }} />
              <Tab label="Overdue" value="overdue" sx={{ minHeight: 0, py: 0.5 }} />
            </Tabs>
          )}
          {tab !== 'wires' && (
            <>
              <TextField
                size="small"
                label="Account ID"
                value={accountFilter}
                onChange={(e) => onFilterChange(setAccountFilter, e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <TextField
                size="small"
                type="date"
                label="From"
                value={startDate}
                onChange={(e) => onFilterChange(setStartDate, e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={endDate}
                onChange={(e) => onFilterChange(setEndDate, e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
          <TextField
            size="small"
            placeholder="Search id / invoice / wire ref / account"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Paper>

      {isLoading ? (
        <Paper sx={{ flex: 1, minHeight: 0 }}>
          <TableSkeleton rows={8} columns={colSpan} />
        </Paper>
      ) : isError ? (
        <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ErrorCard
            title="Failed to load finance data"
            message={(error as Error)?.message}
            onRetry={() => activeQuery.refetch()}
          />
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table sx={{ height: activeRowsEmpty ? '100%' : undefined }}>
            <TableHead>
              {tab === 'payments' && (
                <TableRow>
                  <ResizableTableCell>ID</ResizableTableCell>
                  <ResizableTableCell>Status</ResizableTableCell>
                  <ResizableTableCell>Gross Amount</ResizableTableCell>
                  <ResizableTableCell>Terms</ResizableTableCell>
                  <ResizableTableCell>Payer / Payee</ResizableTableCell>
                  <ResizableTableCell>Invoice</ResizableTableCell>
                  <ResizableTableCell>Due</ResizableTableCell>
                  <ResizableTableCell>Paid</ResizableTableCell>
                  <ResizableTableCell>Created</ResizableTableCell>
                  <ResizableTableCell sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
                </TableRow>
              )}
              {tab === 'payouts' && (
                <TableRow>
                  <ResizableTableCell>ID</ResizableTableCell>
                  <ResizableTableCell>Status</ResizableTableCell>
                  <ResizableTableCell>Amount</ResizableTableCell>
                  <ResizableTableCell>Terms</ResizableTableCell>
                  <ResizableTableCell>Payee</ResizableTableCell>
                  <ResizableTableCell>Due</ResizableTableCell>
                  <ResizableTableCell>Paid</ResizableTableCell>
                  <ResizableTableCell>Created</ResizableTableCell>
                  <ResizableTableCell sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
                </TableRow>
              )}
              {tab === 'wires' && (
                <TableRow>
                  <ResizableTableCell>ID</ResizableTableCell>
                  <ResizableTableCell>Status</ResizableTableCell>
                  <ResizableTableCell>Amount</ResizableTableCell>
                  <ResizableTableCell>Payer</ResizableTableCell>
                  <ResizableTableCell>Bank Reference</ResizableTableCell>
                  <ResizableTableCell>Invoice</ResizableTableCell>
                  <ResizableTableCell>Reminders</ResizableTableCell>
                  <ResizableTableCell>Created</ResizableTableCell>
                  <ResizableTableCell sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {tab === 'payments' && visiblePayments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ borderBottom: 'none', verticalAlign: 'middle' }}>
                    <EmptyState
                      title="No payments match the current filters"
                      description="Adjust the status, account, date range, or search filters above."
                    />
                  </TableCell>
                </TableRow>
              )}
              {tab === 'payments' &&
                visiblePayments.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      <Tooltip title={p.id}>
                        <span>{p.id.substring(0, 12)}…</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getLocalizedLabel('paymentStatus', p.status)}
                        size="small"
                        color={statusColor(p.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {formatCurrency(amountOf(p))}
                      {typeof p.refundedAmount === 'number' && p.refundedAmount > 0 && (
                        <Typography variant="caption" color="error" display="block">
                          -{formatCurrency(p.refundedAmount)} refunded
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {p.terms ? getLocalizedLabel('paymentTerms', p.terms) || p.terms : '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {(p.payerAccountId || '—').substring(0, 8)}…
                      <br />
                      {(p.payeeAccountId || '—').substring(0, 8)}…
                    </TableCell>
                    <TableCell>{p.invoiceNumber || '—'}</TableCell>
                    <TableCell>
                      {p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      {paidDateOf(p)
                        ? new Date(paidDateOf(p) as string).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell sx={stickyActionsBodySx}>
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => handleViewPayment(p.id)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}

              {tab === 'payouts' && visiblePayouts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ borderBottom: 'none', verticalAlign: 'middle' }}>
                    <EmptyState
                      title="No payouts match the current filters"
                      description="Adjust the status, account, date range, or search filters above."
                    />
                  </TableCell>
                </TableRow>
              )}
              {tab === 'payouts' &&
                visiblePayouts.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      <Tooltip title={p.id}>
                        <span>{p.id.substring(0, 12)}…</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getLocalizedLabel('paymentStatus', p.status)}
                        size="small"
                        color={statusColor(p.status)}
                      />
                    </TableCell>
                    <TableCell>{formatCurrency(p.amount)}</TableCell>
                    <TableCell>
                      {p.terms ? getLocalizedLabel('paymentTerms', p.terms) || p.terms : '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {(p.payeeAccountId || '—').substring(0, 8)}…
                    </TableCell>
                    <TableCell>
                      {payoutDueDateOf(p)
                        ? new Date(payoutDueDateOf(p) as string).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {payoutPaidDateOf(p)
                        ? new Date(payoutPaidDateOf(p) as string).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell sx={stickyActionsBodySx}>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => handleViewPayout(p.id)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {(p.status === 'SCHEDULED' || p.status === 'PENDING') && (
                          <Tooltip title="Cancel payout">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedPayout(p)
                                setCancelDialogOpen(true)
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

              {tab === 'wires' && visibleWires.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ borderBottom: 'none', verticalAlign: 'middle' }}>
                    <EmptyState
                      title={`No ${wireSubTab} wire transfers`}
                      description="Try the other sub-tab or clear the search filter."
                    />
                  </TableCell>
                </TableRow>
              )}
              {tab === 'wires' &&
                visibleWires.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      <Tooltip title={p.id}>
                        <span>{p.id.substring(0, 12)}…</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getLocalizedLabel('paymentStatus', p.status)}
                        size="small"
                        color={statusColor(p.status)}
                      />
                    </TableCell>
                    <TableCell>{formatCurrency(amountOf(p))}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {(p.payerAccountId || '—').substring(0, 8)}…
                    </TableCell>
                    <TableCell>{paymentRefOf(p) || '—'}</TableCell>
                    <TableCell>{p.invoiceNumber || '—'}</TableCell>
                    <TableCell>{p.reminderCount ?? 0}</TableCell>
                    <TableCell>
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell sx={stickyActionsBodySx}>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => handleViewPayment(p.id)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reconcile wire transfer">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setReconcilePaymentId(p.id)
                              setReconcileDialogOpen(true)
                            }}
                          >
                            <SyncIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination for paged tabs */}
      {tab === 'payments' && paymentsTotalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={paymentsTotalPages}
            page={page + 1}
            onChange={(_e, v) => setPage(v - 1)}
            color="primary"
          />
        </Box>
      )}
      {tab === 'payouts' && payoutsTotalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={payoutsTotalPages}
            page={page + 1}
            onChange={(_e, v) => setPage(v - 1)}
            color="primary"
          />
        </Box>
      )}

      {/* Detail dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPayment ? 'Payment Details' : 'Payout Details'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayment && (
            <PropertyView>
              <PropertySection title="Identity">
                <PropertyField label="ID" value={selectedPayment.id} mono copyable full />
                <PropertyField label="Status">
                  <Chip
                    label={getLocalizedLabel('paymentStatus', selectedPayment.status)}
                    size="small"
                    color={statusColor(selectedPayment.status)}
                  />
                </PropertyField>
                <PropertyField label="Currency" value={selectedPayment.currency} />
              </PropertySection>
              <PropertySection title="Amounts" columns={3}>
                <PropertyField label="Gross Amount">
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(amountOf(selectedPayment))}
                  </Typography>
                </PropertyField>
                <PropertyField
                  label="System Fee"
                  value={selectedPayment.systemFee != null ? formatCurrency(selectedPayment.systemFee) : null}
                />
                <PropertyField
                  label="Refunded"
                  value={selectedPayment.refundedAmount ? formatCurrency(selectedPayment.refundedAmount) : null}
                />
              </PropertySection>
              <PropertySection title="Billing" columns={3}>
                <PropertyField
                  label="Terms"
                  value={
                    selectedPayment.terms
                      ? getLocalizedLabel('paymentTerms', selectedPayment.terms) || selectedPayment.terms
                      : null
                  }
                />
                <PropertyField label="Invoice #" value={selectedPayment.invoiceNumber} />
                <PropertyField label="Bank Reference" value={selectedPayment.bankWireReference} />
              </PropertySection>
              <PropertySection title="Timing" columns={3}>
                <PropertyField label="Due Date" value={selectedPayment.dueDate} type="datetime" />
                <PropertyField label="Paid Date" value={paidDateOf(selectedPayment)} type="datetime" />
                <PropertyField label="Created" value={selectedPayment.createdAt} type="datetime" />
              </PropertySection>
              <PropertySection title="Parties">
                <PropertyField label="Payer Account" value={selectedPayment.payerAccountId} mono copyable />
                <PropertyField label="Payee Account" value={selectedPayment.payeeAccountId} mono copyable />
              </PropertySection>
              {(selectedPayment.failureReason || selectedPayment.refundReason) && (
                <PropertySection title="Issues">
                  {selectedPayment.failureReason && (
                    <PropertyField label="Failure" full>
                      <Alert severity="error" variant="outlined" sx={{ width: '100%' }}>
                        {selectedPayment.failureReason}
                      </Alert>
                    </PropertyField>
                  )}
                  {selectedPayment.refundReason && (
                    <PropertyField label="Previous refund reason" full>
                      <Alert severity="warning" variant="outlined" sx={{ width: '100%' }}>
                        {selectedPayment.refundReason}
                      </Alert>
                    </PropertyField>
                  )}
                </PropertySection>
              )}
            </PropertyView>
          )}

          {selectedPayout && (
            <PropertyView>
              <PropertySection title="Identity">
                <PropertyField label="ID" value={selectedPayout.id} mono copyable full />
                <PropertyField label="Status">
                  <Chip
                    label={getLocalizedLabel('paymentStatus', selectedPayout.status)}
                    size="small"
                    color={statusColor(selectedPayout.status)}
                  />
                </PropertyField>
                <PropertyField label="Currency" value={selectedPayout.currency} />
              </PropertySection>
              <PropertySection title="Amounts" columns={3}>
                <PropertyField label="Amount">
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(selectedPayout.amount)}
                  </Typography>
                </PropertyField>
                <PropertyField
                  label="Terms"
                  value={
                    selectedPayout.terms
                      ? getLocalizedLabel('paymentTerms', selectedPayout.terms) || selectedPayout.terms
                      : null
                  }
                />
                <PropertyField label="Processor" value={selectedPayout.paymentProcessor} />
              </PropertySection>
              <PropertySection title="Timing" columns={3}>
                <PropertyField label="Due Date" value={payoutDueDateOf(selectedPayout)} type="datetime" />
                <PropertyField label="Paid Date" value={payoutPaidDateOf(selectedPayout)} type="datetime" />
                <PropertyField label="Invoice #" value={selectedPayout.invoiceNumber} />
              </PropertySection>
              <PropertySection title="Parties">
                <PropertyField label="Payee Account" value={selectedPayout.payeeAccountId} mono copyable />
                <PropertyField label="Payer Account" value={selectedPayout.payerAccountId} mono copyable />
              </PropertySection>
              {selectedPayout.failureReason && (
                <PropertySection title="Issues">
                  <PropertyField label="Failure" full>
                    <Alert severity="error" variant="outlined" sx={{ width: '100%' }}>
                      {selectedPayout.failureReason}
                    </Alert>
                  </PropertyField>
                </PropertySection>
              )}
            </PropertyView>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedPayment && selectedPayment.status === 'COMPLETED' && (
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setRefundAmount(String(amountOf(selectedPayment) - (selectedPayment.refundedAmount || 0)))
                setRefundReason('')
                setRefundDialogOpen(true)
              }}
            >
              Refund…
            </Button>
          )}
          {selectedPayout &&
            (selectedPayout.status === 'SCHEDULED' || selectedPayout.status === 'PENDING') && (
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setCancelReason('')
                  setCancelDialogOpen(true)
                }}
              >
                Cancel Payout…
              </Button>
            )}
          {selectedPayment &&
            (selectedPayment.status === 'PENDING' ||
              selectedPayment.status === 'PROCESSING') && (
              <Button
                variant="contained"
                onClick={() => {
                  setReconcilePaymentId(selectedPayment.id)
                  setReconcileRef(selectedPayment.bankWireReference || '')
                  setReconcileDialogOpen(true)
                }}
                startIcon={<SyncIcon />}
              >
                Reconcile…
              </Button>
            )}
        </DialogActions>
      </Dialog>

      {/* Refund dialog */}
      <Dialog
        open={refundDialogOpen}
        onClose={() => setRefundDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Process Refund</DialogTitle>
        <DialogContent dividers>
          {selectedPayment && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                Original amount:{' '}
                <strong>{formatCurrency(amountOf(selectedPayment))}</strong>
                {selectedPayment.refundedAmount ? (
                  <>
                    {' '}· already refunded:{' '}
                    <strong>{formatCurrency(selectedPayment.refundedAmount)}</strong>
                  </>
                ) : null}
              </Alert>
              <TextField
                label="Refund Amount"
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                fullWidth
                size="small"
                inputProps={{
                  min: 0.01,
                  step: 0.01,
                  max: amountOf(selectedPayment),
                }}
                error={!!refundAmount && !refundAmountValid}
                helperText={
                  !refundAmount
                    ? 'Required'
                    : !refundAmountValid
                    ? `Must be between 0.01 and ${amountOf(selectedPayment)}`
                    : ''
                }
              />
              <TextField
                label="Refund Reason"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                fullWidth
                multiline
                rows={3}
                error={!!refundReason && !refundReasonValid}
                helperText={`${refundReason.trim().length}/500 · min 10 chars`}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!refundAmountValid || !refundReasonValid || refundMutation.isPending}
            onClick={() => setRefundConfirmOpen(true)}
          >
            {refundMutation.isPending ? 'Processing…' : 'Process Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel payout dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Payout</DialogTitle>
        <DialogContent dividers>
          {selectedPayout && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                You are about to cancel a payout of{' '}
                <strong>{formatCurrency(selectedPayout.amount)}</strong>. This cannot be
                undone.
              </Typography>
              <TextField
                label="Cancellation Reason *"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Explain why this payout is being cancelled…"
                error={!!cancelReason && !cancelReasonValid}
                helperText={`${cancelReason.trim().length}/500 · min 10 chars`}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Back</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!cancelReasonValid || cancelPayoutMutation.isPending}
            onClick={() => setCancelConfirmOpen(true)}
          >
            {cancelPayoutMutation.isPending ? 'Cancelling…' : 'Cancel Payout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reconcile wire transfer dialog */}
      <Dialog
        open={reconcileDialogOpen}
        onClose={() => setReconcileDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reconcile Wire Transfer</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the bank's reference number from their transaction record.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Bank Reference *"
              value={reconcileRef}
              onChange={(e) => setReconcileRef(e.target.value)}
              fullWidth
              placeholder="e.g. TXN-20240101-123456"
              error={!!reconcileRef && !reconcileRefValid}
              helperText={`${reconcileRef.trim().length}/100 · min 5 chars`}
            />
            <TextField
              label="Notes"
              value={reconcileNotes}
              onChange={(e) => setReconcileNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional notes…"
            />
            <Divider />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReconcileDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SyncIcon />}
            disabled={!reconcileRefValid || reconcileMutation.isPending}
            onClick={() =>
              reconcilePaymentId &&
              reconcileMutation.mutate({
                paymentId: reconcilePaymentId,
                request: {
                  bankReference: reconcileRef.trim(),
                  notes: reconcileNotes.trim() || undefined,
                },
              })
            }
          >
            {reconcileMutation.isPending ? 'Reconciling…' : 'Reconcile'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund — typed confirmation gate */}
      <ConfirmDialog
        open={refundConfirmOpen}
        title="Process refund?"
        description={
          selectedPayment ? (
            <>
              You're about to refund{' '}
              <strong>{formatCurrency(parseFloat(refundAmount) || 0)}</strong> against payment{' '}
              <strong>{selectedPayment.id.substring(0, 8)}</strong>. Refunds are irreversible.
            </>
          ) : (
            'Refunds are irreversible.'
          )
        }
        confirmPhrase={selectedPayment ? selectedPayment.id.substring(0, 8) : 'REFUND'}
        confirmHelper="Type the payment ID prefix shown above to confirm."
        confirmLabel="Process Refund"
        severity="error"
        loading={refundMutation.isPending}
        onClose={() => setRefundConfirmOpen(false)}
        onConfirm={() =>
          selectedPayment &&
          refundMutation.mutate({
            paymentId: selectedPayment.id,
            request: {
              amount: parseFloat(refundAmount),
              reason: refundReason.trim(),
            },
          })
        }
      />

      {/* Cancel payout — typed confirmation gate */}
      <ConfirmDialog
        open={cancelConfirmOpen}
        title="Cancel payout?"
        description={
          selectedPayout ? (
            <>
              You're about to cancel a payout of{' '}
              <strong>{formatCurrency(selectedPayout.amount)}</strong> (
              {selectedPayout.id.substring(0, 8)}). This cannot be undone.
            </>
          ) : (
            'This cannot be undone.'
          )
        }
        confirmPhrase={selectedPayout ? selectedPayout.id.substring(0, 8) : 'CANCEL'}
        confirmHelper="Type the payout ID prefix shown above to confirm."
        confirmLabel="Cancel Payout"
        severity="error"
        loading={cancelPayoutMutation.isPending}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() =>
          selectedPayout &&
          cancelPayoutMutation.mutate({
            payoutId: selectedPayout.id,
            reason: cancelReason.trim(),
          })
        }
      />
    </Box>
  )
}
